<template>
  <div class="scheduling-container" v-if="visible" @click.self="close">
    <div class="scheduling-modal" @click.stop>
      <!-- 头部 -->
      <div class="scheduling-header">
        <div class="header-title">
          <span class="title-icon">⚙️</span>
          <span class="title-text">黄河古贤工程运行调度知识平台</span>
        </div>
        <button class="close-btn" @click="close">×</button>
      </div>

      <!-- 内容区域 -->
      <div class="scheduling-content">
        <!-- 左侧：调度工具区域 -->
        <div class="tools-section">
          <div class="section-header">
            <span class="section-icon">🔧</span>
            <span>调度工具</span>
          </div>

          <!-- 场景选择 -->
          <div class="scenario-selector">
            <h5>📁 选择调度场景</h5>
            <div class="scenario-grid">
              <div
                  v-for="(scenario, index) in scenarios"
                  :key="index"
                  class="scenario-card"
                  :class="{ active: activeScenario === index }"
                  @click="selectScenario(index)"
              >
                <div class="scenario-icon">{{ scenario.icon }}</div>
                <div class="scenario-name">{{ scenario.name }}</div>
              </div>
            </div>
          </div>

          <!-- 参数配置 -->
          <div class="parameter-config">
            <h5>⚙️ 工况参数</h5>
            <div class="param-group">
              <div class="param-item">
                <label>来水频率</label>
                <select v-model="currentScenario.parameters.inflowFrequency" class="custom-select">
                  <option value="丰水">丰水 (P>20%)</option>
                  <option value="平水">平水 (P=20-80%)</option>
                  <option value="枯水">枯水 (P=80-95%)</option>
                  <option value="特枯">特枯 (P>95%)</option>
                </select>
              </div>

              <div class="param-item">
                <label>农业需水 (万m³)</label>
                <input type="number" v-model="currentScenario.parameters.agriculturalDemand">
              </div>

              <div class="param-item">
                <label>生态基流 (m³/s)</label>
                <input type="number" v-model="currentScenario.parameters.ecologicalDemand">
              </div>
            </div>

            <button class="analyze-btn" @click="analyzeScenario">
              <span class="btn-icon">🔍</span>
              智能分析
            </button>
          </div>

          <!-- 历史记录 -->
          <div class="history-section">
            <h5>📅 历史分析记录</h5>
            <div class="history-list">
              <div
                  v-for="(record, index) in historyRecords"
                  :key="index"
                  class="history-item"
                  @click="loadHistory(record)"
              >
                <div class="history-icon">{{ record.icon }}</div>
                <div class="history-content">
                  <div class="history-title">{{ record.title }}</div>
                  <div class="history-time">{{ record.time }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：AI知识问答区 -->
        <div class="ai-assistant-section">
          <div class="section-header">
            <span class="section-icon">🤖</span>
            <span>AI调度助手</span>
          </div>

          <!-- 消息列表 -->
          <div class="messages-container" ref="messagesContainer">
            <!-- 对话消息 -->
            <div v-for="(message, index) in messages" :key="index" class="message-wrapper">
              <!-- 用户消息 -->
              <div v-if="message.role === 'user'" class="message user-message">
                <div class="message-avatar">👤</div>
                <div class="message-content">
                  <div class="message-text">{{ message.content }}</div>
                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>

              <!-- AI消息 -->
              <div v-if="message.role === 'assistant'" class="message assistant-message">
                <div class="message-avatar">🤖</div>
                <div class="message-content">
                  <!-- 调度建议卡片 -->
                  <div v-if="message.type === 'scheduling_advice'" class="scheduling-card">
                    <div class="card-header">
                      <span class="card-title">📋 调度建议</span>
                      <span class="card-subtitle">基于当前场景和参数的智能分析</span>
                    </div>

                    <!-- 文字分析 -->
                    <div class="text-analysis">
                      <div class="section-title">
                        <span class="section-icon">📋</span>
                        调度策略分析
                      </div>
                      <div class="analysis-content">
                        <p>{{ message.data.strategySummary }}</p>
                      </div>

                      <div class="strategy-actions">
                        <div class="section-title">
                          <span class="section-icon">🔧</span>
                          推荐措施
                        </div>
                        <div class="actions-list">
                          <div v-for="(action, idx) in message.data.actions" :key="'action-'+idx" class="action-item">
                            <span class="action-icon">→</span>
                            <span class="action-text">{{ action }}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 可视化分析区域 - 修改为可水平滚动 -->
                    <div class="chart-analysis" v-if="message.data.chartData">
                      <div class="section-title">
                        <span class="section-icon">📊</span>
                        可视化分析
                      </div>

                      <!-- 添加水平滚动容器 -->
                      <div class="chart-scroll-container">
                        <div class="charts-container">
                          <!-- 调度方案对比柱状图 -->
                          <div class="chart-card">
                            <div class="chart-title">{{ message.data.chartData.chart1Title }}</div>
                            <div class="chart-wrapper">
                              <svg class="comparison-chart" :width="getChartWidth(message.data.chartData.comparisonData.length)" height="180">
                                <!-- 网格线 -->
                                <g class="grid-lines">
                                  <line x1="0" y1="20" :x2="getChartWidth(message.data.chartData.comparisonData.length)" y2="20" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="60" :x2="getChartWidth(message.data.chartData.comparisonData.length)" y2="60" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="100" :x2="getChartWidth(message.data.chartData.comparisonData.length)" y2="100" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="140" :x2="getChartWidth(message.data.chartData.comparisonData.length)" y2="140" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                </g>

                                <!-- 柱状图 -->
                                <g v-for="(plan, index) in message.data.chartData.comparisonData" :key="index" class="bar-group">
                                  <rect
                                      :x="60 + index * 120"
                                      :y="160 - plan.value * 1.2"
                                      width="60"
                                      :height="plan.value * 1.2"
                                      rx="4"
                                      :class="'bar bar-' + (index + 1)"
                                      :fill="plan.color"
                                  />
                                  <text
                                      :x="90 + index * 120"
                                      y="170"
                                      text-anchor="middle"
                                      class="bar-label"
                                      font-size="12"
                                  >{{ plan.name }}</text>
                                  <text
                                      :x="90 + index * 120"
                                      :y="150 - plan.value * 1.2"
                                      text-anchor="middle"
                                      class="bar-value"
                                      font-size="11"
                                      font-weight="bold"
                                  >{{ plan.value }}%</text>
                                </g>

                                <!-- 坐标轴 -->
                                <g class="axes">
                                  <line x1="50" y1="20" x2="50" y2="160" stroke="#0066cc" stroke-width="2" />
                                  <line x1="50" y1="160" :x2="getChartWidth(message.data.chartData.comparisonData.length)" y2="160" stroke="#0066cc" stroke-width="2" />
                                </g>

                                <!-- 标签 -->
                                <text :x="getChartWidth(message.data.chartData.comparisonData.length) / 2" y="180" text-anchor="middle" class="axis-label">{{ message.data.chartData.xAxisLabel }}</text>
                                <text x="20" y="90" text-anchor="middle" transform="rotate(-90, 20, 90)" class="axis-label">{{ message.data.chartData.yAxisLabel }}</text>
                              </svg>
                            </div>
                            <div class="chart-legend" v-if="message.data.chartData.legendData">
                              <div v-for="(item, index) in message.data.chartData.legendData" :key="index" class="legend-item">
                                <span class="legend-color" :style="{backgroundColor: item.color}"></span>
                                <span class="legend-text">{{ item.text }}</span>
                              </div>
                            </div>
                          </div>

                          <!-- 历史趋势折线图 -->
                          <div class="chart-card">
                            <div class="chart-title">{{ message.data.chartData.chart2Title }}</div>
                            <div class="chart-wrapper">
                              <svg class="trend-chart" :width="getChartWidth(6)" height="180">
                                <!-- 数据线 -->
                                <path
                                    d="M 60,120 L 120,100 L 180,85 L 240,110 L 300,95 L 360,80"
                                    fill="none"
                                    stroke="#0066cc"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                />

                                <!-- 数据点 -->
                                <circle cx="60" cy="120" r="4" fill="#0066cc" />
                                <circle cx="120" cy="100" r="4" fill="#0066cc" />
                                <circle cx="180" cy="85" r="4" fill="#0066cc" />
                                <circle cx="240" cy="110" r="4" fill="#0066cc" />
                                <circle cx="300" cy="95" r="4" fill="#0066cc" />
                                <circle cx="360" cy="80" r="4" fill="#0066cc" />

                                <!-- 网格线 -->
                                <g class="grid-lines">
                                  <line x1="0" y1="40" :x2="getChartWidth(6)" y2="40" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="80" :x2="getChartWidth(6)" y2="80" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="120" :x2="getChartWidth(6)" y2="120" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                  <line x1="0" y1="160" :x2="getChartWidth(6)" y2="160" stroke="#b3e0ff" stroke-width="1" stroke-dasharray="5,5" />
                                </g>

                                <!-- 年份标签 -->
                                <text x="60" y="175" text-anchor="middle" class="year-label">2004</text>
                                <text x="120" y="175" text-anchor="middle" class="year-label">2007</text>
                                <text x="180" y="175" text-anchor="middle" class="year-label">2010</text>
                                <text x="240" y="175" text-anchor="middle" class="year-label">2013</text>
                                <text x="300" y="175" text-anchor="middle" class="year-label">2016</text>
                                <text x="360" y="175" text-anchor="middle" class="year-label">2019</text>
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 依据和案例 -->
                    <div class="reference-section">
                      <div class="reference-item">
                        <div class="section-title">
                          <span class="section-icon">📄</span>
                          依据条款
                        </div>
                        <div class="reference-content">
                          <div class="regulation-item" v-for="(reg, index) in message.data.regulations" :key="index">
                            <span class="reg-code">{{ reg.code }}</span>
                            <span class="reg-content">{{ reg.content }}</span>
                          </div>
                        </div>
                      </div>

                      <div class="reference-item">
                        <div class="section-title">
                          <span class="section-icon">🏗️</span>
                          参考案例
                        </div>
                        <div class="reference-content">
                          <div class="case-item" v-for="(caseItem, index) in message.data.cases" :key="index">
                            <span class="case-name">{{ caseItem.name }}</span>
                            <span class="case-result">{{ caseItem.result }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 普通文本回复 -->
                  <div v-else class="message-text">{{ message.content }}</div>

                  <div class="message-time">{{ message.time }}</div>
                </div>
              </div>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="loading-message">
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span class="loading-text">正在检索调度知识库...</span>
            </div>
          </div>

          <!-- 输入区域 -->
          <div class="input-area">
            <div class="input-tools">
              <button
                  v-for="(tool, idx) in inputTools"
                  :key="'tool-'+idx"
                  class="tool-btn"
                  :title="tool.title"
                  @click="useTool(tool.action)"
              >
                {{ tool.icon }}
              </button>
            </div>
            <div class="input-wrapper">
              <textarea
                  v-model="userInput"
                  placeholder="请输入调度相关问题，如：特枯来水情况下如何制定供水调度方案？"
                  @keydown.enter.exact.prevent="sendMessage"
                  @keydown.enter.shift.exact.prevent="userInput += '\n'"
                  rows="3"
                  class="message-input"
                  ref="messageInput"
              ></textarea>
              <button class="send-btn" @click="sendMessage" :disabled="!userInput.trim()">
                <span class="send-icon">↗</span>
              </button>
            </div>

            <!-- 快速提问区域 - 标签和按钮在同一行 -->
            <div class="quick-actions">
              <span class="quick-label">快速查询：</span>
              <div class="quick-buttons">
                <button
                    v-for="(action, idx) in quickActions"
                    :key="'action-'+idx"
                    class="quick-btn"
                    @click="quickQuery(action.query)"
                >
                  {{ action.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OperationSchedulingQA',
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    initialQuery: {
      type: String,
      default: ''
    },
    source: {
      type: String,
      default: ''
    },
    smartOrbHandoff: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      userInput: '',
      lastInitialQuery: '',
      messages: [
        {
          role: 'user',
          content: '特枯来水情况下如何制定供水调度方案？',
          time: '10:30'
        },
        {
          role: 'assistant',
          type: 'scheduling_advice',
          content: '根据特枯来水情况，系统提供以下调度建议：',
          time: '10:31',
          data: {
            strategySummary: '根据《黄河流域水资源调度管理办法》和《古贤水利枢纽工程调度规程》，针对P>95%的特枯来水条件，应采取"保生活、压工业、限农业"的调度原则，优先保障城乡基本生活用水和河道生态基流。',
            actions: [
              '启动《特枯水情应急调度预案》Ⅲ级响应',
              '实施灌区轮灌制度，将农业用水控制在正常年份的60%以内',
              '动用死库容，日补充供水8万m³，保障关键期用水',
              '加强水质监测，防止咸潮上溯影响供水安全',
              '协调上下游用水，实施流域统一调度'
            ],
            regulations: [
              {
                code: 'SL 706-2015 第4.3.2条',
                content: '当遭遇特枯水情时，应优先保障城镇居民生活用水和重要河湖生态流量，农业灌溉用水可按供水保证率适当核减'
              },
              {
                code: '《黄河流域水资源调度管理办法》第二十四条',
                content: '枯水期和特枯水情时，流域管理机构应当组织实施应急调度，优先保障生活、生态和重要工业用水'
              }
            ],
            cases: [
              {
                name: '2019-2020年黄河下游特枯水情应对',
                result: '通过小浪底水库精准调度，保障了郑州、济南等城市供水安全，农业用水保证率达到85%，未发生重大用水纠纷'
              },
              {
                name: '2002年黄河全流域特枯水情调度',
                result: '实施跨省区水量统一调度，首次实现黄河不断流，保障了河口三角洲生态用水'
              }
            ],
            chartData: {
              chart1Title: '特枯水情调度方案对比',
              chart2Title: '历史特枯年份供水保证率趋势',
              xAxisLabel: '调度方案',
              yAxisLabel: '供水保证率(%)',
              comparisonData: [
                {name: '保守型', value: 85, color: '#4a90e2'},
                {name: '均衡型', value: 92, color: '#50c878'},
                {name: '激进型', value: 78, color: '#ff6b6b'}
              ],
              legendData: [
                {color: '#4a90e2', text: '保守型 (低风险)'},
                {color: '#50c878', text: '均衡型 (中风险)'},
                {color: '#ff6b6b', text: '激进型 (高风险)'}
              ]
            }
          }
        }
      ],
      loading: false,
      activeScenario: 0,
      currentScenario: {
        name: '极端枯水调度',
        parameters: {
          inflowFrequency: '特枯',
          agriculturalDemand: 2800,
          ecologicalDemand: 15
        }
      },
      scenarios: [
        {icon: '🌵', name: '极端枯水'},
        {icon: '🌧️', name: '丰水调度'},
        {icon: '⚡', name: '设备故障'},
        {icon: '🌾', name: '灌溉高峰'}
      ],
      historyRecords: [
        {
          icon: '🌵',
          title: '特枯来水调度方案',
          time: '今天 10:30',
          data: {
            userQuestion: '极端枯水条件下如何保障供水安全？',
            strategySummary: '根据《黄河流域水资源调度管理办法》和《古贤水利枢纽工程调度规程》，针对P>95%的特枯来水条件，应采取"保生活、压工业、限农业"的调度原则，优先保障城乡基本生活用水和河道生态基流。',
            actions: [
              '启动《特枯水情应急调度预案》Ⅲ级响应',
              '实施灌区轮灌制度，将农业用水控制在正常年份的60%以内',
              '动用死库容，日补充供水8万m³，保障关键期用水',
              '加强水质监测，防止咸潮上溯影响供水安全',
              '协调上下游用水，实施流域统一调度'
            ],
            regulations: [
              {
                code: 'SL 706-2015 第4.3.2条',
                content: '当遭遇特枯水情时，应优先保障城镇居民生活用水和重要河湖生态流量，农业灌溉用水可按供水保证率适当核减'
              },
              {
                code: '《黄河流域水资源调度管理办法》第二十四条',
                content: '枯水期和特枯水情时，流域管理机构应当组织实施应急调度，优先保障生活、生态和重要工业用水'
              }
            ],
            cases: [
              {
                name: '2019-2020年黄河下游特枯水情应对',
                result: '通过小浪底水库精准调度，保障了郑州、济南等城市供水安全，农业用水保证率达到85%，未发生重大用水纠纷'
              },
              {
                name: '2002年黄河全流域特枯水情调度',
                result: '实施跨省区水量统一调度，首次实现黄河不断流，保障了河口三角洲生态用水'
              }
            ],
            chartData: {
              chart1Title: '特枯水情调度方案对比',
              chart2Title: '历史特枯年份供水保证率趋势',
              xAxisLabel: '调度方案',
              yAxisLabel: '供水保证率(%)',
              comparisonData: [
                {name: '保守型', value: 85, color: '#4a90e2'},
                {name: '均衡型', value: 92, color: '#50c878'},
                {name: '激进型', value: 78, color: '#ff6b6b'}
              ],
              legendData: [
                {color: '#4a90e2', text: '保守型 (低风险)'},
                {color: '#50c878', text: '均衡型 (中风险)'},
                {color: '#ff6b6b', text: '激进型 (高风险)'}
              ]
            }
          }
        }
      ],
      inputTools: [
        {icon: '📊', title: '数据图表', action: 'show_chart'},
        {icon: '📄', title: '规程查询', action: 'search_regulation'},
        {icon: '📈', title: '趋势分析', action: 'trend_analysis'},
        {icon: '🔍', title: '方案对比', action: 'compare_plan'},
        {icon: '📋', title: '生成报告', action: 'generate_report'}
      ],
      quickActions: [
        {label: '枯水调度', query: '枯水调度方案'},
        {label: '规程查询', query: '相关调度规程'},
        {label: '风险评估', query: '调度风险评估'},
        {label: '方案对比', query: '不同调度方案对比'},
        {label: '历史案例', query: '类似历史案例'}
      ]
    }
  },
  watch: {
    initialQuery: {
      immediate: true,
      handler(val) {
        const query = String(val || '').trim()
        if (!query || query === this.lastInitialQuery) return
        this.lastInitialQuery = query
        this.$nextTick(() => {
          this.userInput = query
          this.sendMessage()
        })
      }
    },
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.scrollToBottom()
          this.$refs.messageInput?.focus()
        })
      }
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  },
  methods: {
    getCurrentTime() {
      const now = new Date()
      return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    },

    getChartWidth(dataCount) {
      // 根据数据数量动态计算图表宽度
      // 每个数据项占120px，基础宽度为100px
      return Math.max(400, 100 + dataCount * 120)
    },

    selectScenario(index) {
      this.activeScenario = index
      this.currentScenario.name = this.scenarios[index].name

      // 根据场景更新参数
      switch (index) {
        case 0: // 极端枯水
          this.currentScenario.parameters = {
            inflowFrequency: '特枯',
            agriculturalDemand: 2800,
            ecologicalDemand: 15
          }
          break
        case 1: // 丰水调度
          this.currentScenario.parameters = {
            inflowFrequency: '丰水',
            agriculturalDemand: 3500,
            ecologicalDemand: 25
          }
          break
        case 2: // 设备故障
          this.currentScenario.parameters = {
            inflowFrequency: '平水',
            agriculturalDemand: 3000,
            ecologicalDemand: 20
          }
          break
        case 3: // 灌溉高峰
          this.currentScenario.parameters = {
            inflowFrequency: '枯水',
            agriculturalDemand: 4000,
            ecologicalDemand: 18
          }
          break
      }
    },

    analyzeScenario() {
      const scenario = this.scenarios[this.activeScenario]
      const question = `${scenario.name}场景下，${this.currentScenario.parameters.inflowFrequency}来水，农业需水${this.currentScenario.parameters.agriculturalDemand}万m³，生态基流${this.currentScenario.parameters.ecologicalDemand}m³/s，请给出调度建议`

      this.userInput = question
      this.sendMessage()
    },

    async sendMessage() {
      const question = this.userInput.trim()
      if (!question) return

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content: question,
        time: this.getCurrentTime()
      })

      this.userInput = ''
      this.loading = true

      // 模拟AI响应
      setTimeout(() => {
        // 根据问题类型生成不同的响应
        let responseData = null

        if (question.includes('特枯') || question.includes('枯水')) {
          responseData = {
            type: 'scheduling_advice',
            content: '根据特枯来水情况，系统提供以下调度建议：',
            data: {
              strategySummary: '根据《黄河流域水资源调度管理办法》和《古贤水利枢纽工程调度规程》，针对P>95%的特枯来水条件，应采取"保生活、压工业、限农业"的调度原则，优先保障城乡基本生活用水和河道生态基流。',
              actions: [
                '启动《特枯水情应急调度预案》Ⅲ级响应',
                '实施灌区轮灌制度，将农业用水控制在正常年份的60%以内',
                '动用死库容，日补充供水8万m³，保障关键期用水',
                '加强水质监测，防止咸潮上溯影响供水安全',
                '协调上下游用水，实施流域统一调度'
              ],
              regulations: [
                {
                  code: 'SL 706-2015 第4.3.2条',
                  content: '当遭遇特枯水情时，应优先保障城镇居民生活用水和重要河湖生态流量，农业灌溉用水可按供水保证率适当核减'
                },
                {
                  code: '《黄河流域水资源调度管理办法》第二十四条',
                  content: '枯水期和特枯水情时，流域管理机构应当组织实施应急调度，优先保障生活、生态和重要工业用水'
                }
              ],
              cases: [
                {
                  name: '2019-2020年黄河下游特枯水情应对',
                  result: '通过小浪底水库精准调度，保障了郑州、济南等城市供水安全，农业用水保证率达到85%，未发生重大用水纠纷'
                },
                {
                  name: '2002年黄河全流域特枯水情调度',
                  result: '实施跨省区水量统一调度，首次实现黄河不断流，保障了河口三角洲生态用水'
                }
              ],
              chartData: {
                chart1Title: '特枯水情调度方案对比',
                chart2Title: '历史特枯年份供水保证率趋势',
                xAxisLabel: '调度方案',
                yAxisLabel: '供水保证率(%)',
                comparisonData: [
                  {name: '保守型', value: 85, color: '#4a90e2'},
                  {name: '均衡型', value: 92, color: '#50c878'},
                  {name: '激进型', value: 78, color: '#ff6b6b'}
                ],
                legendData: [
                  {color: '#4a90e2', text: '保守型 (低风险)'},
                  {color: '#50c878', text: '均衡型 (中风险)'},
                  {color: '#ff6b6b', text: '激进型 (高风险)'}
                ]
              }
            }
          }
        } else if (question.includes('丰水') || question.includes('汛期')) {
          responseData = {
            type: 'scheduling_advice',
            content: '根据丰水情况，系统提供以下调度建议：',
            data: {
              strategySummary: '根据《黄河古贤水利枢纽防洪调度规程》，丰水期应采取"防洪优先、兼顾兴利"的原则，合理利用防洪库容，发挥水库的调洪削峰作用。',
              actions: [
                '启动汛期调度预案，预留防洪库容',
                '根据水文预报实施预泄调度',
                '协调上下游防洪调度，确保防洪安全',
                '在确保防洪安全的前提下，适度增加发电和供水',
                '加强水文监测和预警预报'
              ],
              regulations: [
                {
                  code: 'SL 252-2017 第5.2.1条',
                  content: '水库汛期调度应以防洪安全为首要目标，根据设计洪水标准和调度规则进行调洪计算'
                },
                {
                  code: '《黄河洪水调度管理办法》第十八条',
                  content: '汛期水库调度应实行预报调度，根据洪水预报提前预泄，为后续洪水预留库容'
                }
              ],
              cases: [
                {
                  name: '2021年黄河秋汛调度',
                  result: '通过小浪底、三门峡等水库联合调度，成功将花园口洪峰流量从7600m³/s削减至4800m³/s，保障了下游防洪安全'
                },
                {
                  name: '2018年黄河主汛期调度',
                  result: '实施"一高一低"调度策略，主汛期前降低水位运行，为防洪预留库容，汛后蓄水保障供水发电'
                }
              ],
              chartData: {
                chart1Title: '丰水期调度方案对比',
                chart2Title: '近年丰水年份洪水调蓄效果',
                xAxisLabel: '调度方案',
                yAxisLabel: '削峰比例(%)',
                comparisonData: [
                  {name: '方案A', value: 75, color: '#4a90e2'},
                  {name: '方案B', value: 88, color: '#50c878'},
                  {name: '方案C', value: 92, color: '#ff6b6b'}
                ],
                legendData: [
                  {color: '#4a90e2', text: '方案A (常规调度)'},
                  {color: '#50c878', text: '方案B (优化调度)'},
                  {color: '#ff6b6b', text: '方案C (联合调度)'}
                ]
              }
            }
          }
        } else {
          // 普通回复
          responseData = {
            content: '根据《黄河古贤水利枢纽调度规程》，在当前工况下建议：1. 控制下泄流量在合理范围；2. 优先保障生态基流；3. 根据来水预报调整调度计划。',
            time: this.getCurrentTime()
          }
        }

        if (responseData.type === 'scheduling_advice') {
          this.messages.push({
            role: 'assistant',
            type: 'scheduling_advice',
            content: responseData.content,
            time: this.getCurrentTime(),
            data: responseData.data
          })
        } else {
          this.messages.push({
            role: 'assistant',
            content: responseData.content,
            time: this.getCurrentTime()
          })
        }

        this.loading = false

        // 添加到历史记录
        this.historyRecords.unshift({
          icon: this.scenarios[this.activeScenario].icon,
          title: `${this.scenarios[this.activeScenario].name}分析`,
          time: '刚刚',
          data: responseData.type === 'scheduling_advice' ? responseData.data : null
        })
      }, 1500)
    },

    quickQuery(query) {
      this.userInput = query
      this.sendMessage()
    },

    useTool(action) {
      const toolActions = {
        'show_chart': '请展示调度曲线图表',
        'search_regulation': '查询相关调度规程',
        'trend_analysis': '进行历史趋势分析',
        'compare_plan': '对比不同调度方案',
        'generate_report': '生成调度分析报告'
      }

      this.userInput = toolActions[action] || action
      this.sendMessage()
    },

    loadHistory(record) {
      if (record.data) {
        this.messages.push(
            {
              role: 'user',
              content: record.data.userQuestion || '查看历史调度分析',
              time: record.time
            },
            {
              role: 'assistant',
              type: 'scheduling_advice',
              content: '以下是历史调度分析结果：',
              time: '刚刚',
              data: record.data
            }
        )
      }
    },

    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    close() {
      this.$emit('update:visible', false)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* 容器样式 - 浅蓝色渐变背景 */
.scheduling-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 模态框样式 - 浅蓝色渐变背景，蓝色边框 */
.scheduling-modal {
  width: 1200px;
  height: 700px;
  background: linear-gradient(135deg, #e6f2ff 0%, #cce6ff 100%);
  border-radius: 12px;
  border: 1px solid #66b3ff;
  box-shadow: 0 0 30px rgba(102, 179, 255, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 头部样式 */
.scheduling-header {
  height: 60px;
  background: linear-gradient(90deg, #0066cc 0%, #004d99 100%);
  border-bottom: 1px solid #66b3ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
  color: #ffffff;
}

.title-text {
  font-size: 18px;
  font-weight: bold;
  color: #ffffff;
  text-shadow: 0 0 10px rgba(102, 179, 255, 0.5);
}

.close-btn {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #66b3ff;
  border-radius: 50%;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.close-btn:hover {
  background: rgba(102, 179, 255, 0.2);
  box-shadow: 0 0 10px rgba(102, 179, 255, 0.5);
  transform: rotate(90deg);
}

/* 内容区域 */
.scheduling-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧工具区域 - 浅蓝色背景 */
.tools-section {
  width: 300px;
  background: linear-gradient(135deg, #e6f2ff, #d9ebff);
  border-right: 1px solid #b3e0ff;
  display: flex;
  flex-direction: column;
  padding: 15px;
  gap: 20px;
  overflow-y: auto;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: bold;
  color: #0066cc;
  margin-bottom: 8px;
}

.section-icon {
  font-size: 20px;
}

.scenario-selector h5,
.parameter-config h5,
.history-section h5 {
  color: #0066cc;
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.scenario-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
}

.scenario-card {
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 6px;
  padding: 12px 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.scenario-card:hover {
  border-color: #0066cc;
  box-shadow: 0 0 10px rgba(0, 102, 204, 0.1);
  transform: translateY(-2px);
}

.scenario-card.active {
  background: #cce6ff;
  border-color: #0066cc;
  box-shadow: 0 0 8px rgba(0, 102, 204, 0.2);
}

.scenario-icon {
  font-size: 20px;
}

.scenario-name {
  font-size: 12px;
  color: #0066cc;
  font-weight: 500;
}

.parameter-config {
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  padding: 15px;
}

.param-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.param-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.param-item label {
  font-size: 12px;
  color: #0066cc;
  font-weight: 500;
}

.param-item input,
.param-item select {
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #b3e0ff;
  border-radius: 4px;
  color: #333333;
  font-size: 12px;
  transition: all 0.3s;
}

.param-item input:focus,
.param-item select:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 8px rgba(0, 102, 204, 0.2);
  background: #f7fbff;
}

.analyze-btn {
  width: 100%;
  background: linear-gradient(135deg, #0066cc, #004d99);
  border: none;
  padding: 10px;
  border-radius: 6px;
  color: #ffffff;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s;
}

.analyze-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 102, 204, 0.4);
}

.btn-icon {
  font-size: 16px;
}

.history-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  padding: 15px;
}

.history-list {
  flex: 1;
  overflow-y: auto;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #d9ebff;
  border: 1px solid #c2dfff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  min-height: 50px;
}

.history-item:hover {
  background: #cce6ff;
  border-color: #b3e0ff;
  transform: translateX(5px);
}

.history-icon {
  font-size: 20px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #0066cc;
}

.history-content {
  flex: 1;
  min-width: 0;
}

.history-title {
  font-size: 13px;
  color: #0066cc;
  font-weight: 500;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-time {
  font-size: 11px;
  color: #6699cc;
}

/* 右侧AI助手区域 */
.ai-assistant-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(135deg, #f0f9ff, #e6f2ff);
}

.messages-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: transparent;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: messageAppear 0.3s ease;
}

@keyframes messageAppear {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message {
  display: flex;
  gap: 12px;
  max-width: 80%;
  align-items: flex-start;
}

.user-message {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.assistant-message {
  align-self: flex-start;
}

.message-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4da6ff, #0066cc);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  color: #ffffff;
}

.user-message .message-avatar {
  background: linear-gradient(135deg, #66b3ff, #3399ff);
  color: #ffffff;
}

.message-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.user-message .message-content {
  align-items: flex-end;
}

.assistant-message .message-content {
  align-items: flex-start;
}

.user-message .message-text {
  background: linear-gradient(135deg, #66b3ff, #3399ff);
  border: 1px solid #4da6ff;
  color: #ffffff;
  border-radius: 12px 12px 0 12px;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.assistant-message .message-text {
  background: linear-gradient(135deg, #e6f2ff, #d9ebff);
  border: 1px solid #b3e0ff;
  color: #0066cc;
  border-radius: 12px 12px 12px 0;
  padding: 12px 16px;
  line-height: 1.6;
  font-size: 14px;
  word-break: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.message-time {
  font-size: 12px;
  color: #6699cc;
  margin-top: 4px;
}

/* 调度建议卡片样式 */
.scheduling-card {
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 8px;
  width: 100%;
  animation: cardAppear 0.5s ease;
}

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card-header {
  padding: 15px 20px;
  background: linear-gradient(90deg, #d9ebff, #cce6ff);
  border-bottom: 1px solid #b3e0ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.card-title {
  color: #0066cc;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-subtitle {
  color: #6699cc;
  font-size: 12px;
  font-style: italic;
}

.text-analysis {
  padding: 15px 20px;
  border-bottom: 1px solid #cce6ff;
  transition: all 0.3s;
}

.text-analysis:hover {
  background: #d9ebff;
}

.section-title {
  color: #0066cc;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-icon {
  font-size: 16px;
  margin-right: 6px;
}

.analysis-content {
  margin: 8px 0;
  line-height: 1.5;
  color: #0066cc;
  font-size: 13px;
  padding: 12px;
  background: #d9ebff;
  border-radius: 6px;
  border-left: 3px solid #0066cc;
}

.strategy-actions {
  margin-top: 12px;
}

.actions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #0066cc;
  line-height: 1.5;
}

.action-icon {
  color: #0066cc;
  font-weight: bold;
  margin-top: 2px;
  flex-shrink: 0;
}

.action-text {
  flex: 1;
  line-height: 1.4;
}

/* 图表样式 - 主要修改部分 */
.chart-analysis {
  padding: 15px 20px;
  border-bottom: 1px solid #cce6ff;
  transition: all 0.3s;
}

.chart-analysis:hover {
  background: #d9ebff;
}

/* 新增：可视化分析滚动容器 */
.chart-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 10px;
  margin-top: 12px;
  border-radius: 8px;
  background: #f0f9ff;
  border: 1px solid #b3e0ff;
  min-height: 250px;
}

.chart-scroll-container::-webkit-scrollbar {
  height: 8px;
  background: #e6f2ff;
  border-radius: 4px;
}

.chart-scroll-container::-webkit-scrollbar-track {
  background: #e6f2ff;
  border-radius: 4px;
  margin: 0 5px;
}

.chart-scroll-container::-webkit-scrollbar-thumb {
  background: #66b3ff;
  border-radius: 4px;
  border: 2px solid #e6f2ff;
}

.chart-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #3399ff;
}

/* 修改charts-container为横向flex布局 */
.charts-container {
  display: flex;
  gap: 20px;
  padding: 10px;
  min-width: min-content; /* 确保容器不会被压缩 */
  flex-wrap: nowrap; /* 确保不换行 */
}

.chart-card {
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s;
  min-width: 500px; /* 设置最小宽度确保卡片不会被压缩 */
  flex-shrink: 0; /* 防止卡片被压缩 */
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chart-card:hover {
  border-color: #66b3ff;
  box-shadow: 0 0 10px rgba(102, 179, 255, 0.2);
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  color: #0066cc;
  margin-bottom: 10px;
  text-align: center;
  width: 100%;
}

/* 图表包装器 */
.chart-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  width: auto;
  overflow: visible;
  margin-bottom: 10px;
}

.comparison-chart,
.trend-chart {
  font-family: Arial, sans-serif;
  overflow: visible;
}

.grid-lines line {
  stroke: #b3e0ff;
  stroke-width: 0.5;
  stroke-dasharray: 3, 3;
}

.axes line {
  stroke: #0066cc;
  stroke-width: 1.5;
}

.bar-label {
  font-size: 10px;
  fill: #6699cc;
}

.bar-value {
  font-size: 9px;
  font-weight: bold;
  fill: #0066cc;
}

.axis-label {
  font-size: 9px;
  fill: #6699cc;
}

.year-label {
  font-size: 9px;
  fill: #6699cc;
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 10px;
  flex-wrap: wrap;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #0066cc;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* 依据和案例样式 */
.reference-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 15px 20px;
  border-bottom: 1px solid #cce6ff;
  transition: all 0.3s;
}

.reference-section:hover {
  background: #d9ebff;
}

.reference-item {
  background: #e6f2ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.3s;
}

.reference-item:hover {
  border-color: #66b3ff;
  box-shadow: 0 0 10px rgba(102, 179, 255, 0.1);
}

.reference-content {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.regulation-item,
.case-item {
  padding: 12px;
  background: #d9ebff;
  border-radius: 6px;
  transition: all 0.3s;
  animation: itemAppear 0.3s ease;
}

@keyframes itemAppear {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.regulation-item:hover,
.case-item:hover {
  background: #cce6ff;
  transform: translateX(5px);
}

.regulation-item {
  border-left: 3px solid #0066cc;
}

.case-item {
  border-left: 3px solid #339933;
}

.reg-code {
  font-size: 12px;
  font-weight: bold;
  color: #0066cc;
  background: rgba(0, 102, 204, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 6px;
}

.reg-content {
  font-size: 12px;
  color: #0066cc;
  line-height: 1.5;
}

.case-name {
  font-size: 13px;
  font-weight: bold;
  color: #339933;
  margin-bottom: 4px;
  display: block;
}

.case-result {
  font-size: 12px;
  color: #0066cc;
  line-height: 1.5;
}

/* 加载中 */
.loading-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px;
  justify-content: center;
  color: #0066cc;
  font-size: 14px;
  align-self: center;
  width: 100%;
  text-align: center;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0066cc;
  animation: dotFlashing 1s infinite alternate;
  box-shadow: 0 0 8px rgba(0, 102, 204, 0.5);
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotFlashing {
  0% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  100% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.loading-text {
  color: #0066cc;
  font-size: 14px;
  text-shadow: 0 0 8px rgba(0, 102, 204, 0.5);
}

/* 输入区域 */
.input-area {
  padding: 15px 20px;
  background: #e6f2ff;
  border-top: 1px solid #b3e0ff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.input-tools {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.tool-btn {
  width: 36px;
  height: 36px;
  background: #d9ebff;
  border: 1px solid #b3e0ff;
  border-radius: 6px;
  color: #0066cc;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.tool-btn:hover {
  background: #cce6ff;
  border-color: #0066cc;
  box-shadow: 0 0 8px rgba(0, 102, 204, 0.2);
  transform: translateY(-2px);
}

.input-wrapper {
  position: relative;
  margin-bottom: 10px;
}

.message-input {
  width: 100%;
  min-height: 60px;
  max-height: 120px;
  padding: 12px 50px 12px 15px;
  background: #f0f9ff;
  border: 1px solid #b3e0ff;
  border-radius: 8px;
  color: #0066cc;
  font-size: 14px;
  resize: none;
  outline: none;
  transition: all 0.3s;
  line-height: 1.5;
  font-family: inherit;
}

.message-input:focus {
  border-color: #0066cc;
  box-shadow: 0 0 10px rgba(0, 102, 204, 0.2);
  background: #ffffff;
}

.message-input::placeholder {
  color: #99c2ff;
  opacity: 0.7;
}

.send-btn {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #0066cc, #004d99);
  border: none;
  border-radius: 50%;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.send-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #3399ff, #0066cc);
  box-shadow: 0 0 15px rgba(0, 102, 204, 0.5);
  transform: translateY(-2px) scale(1.1);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.send-icon {
  font-size: 20px;
  font-weight: bold;
  transition: all 0.3s;
}

/* 快速提问区域 */
.quick-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-label {
  color: #0066cc;
  font-size: 12px;
  white-space: nowrap;
}

.quick-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
}

.quick-btn {
  padding: 6px 12px;
  background: #d9ebff;
  border: 1px solid #b3e0ff;
  border-radius: 16px;
  color: #0066cc;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.quick-btn:hover {
  background: #cce6ff;
  border-color: #0066cc;
  box-shadow: 0 0 8px rgba(0, 102, 204, 0.2);
  transform: translateY(-1px);
}

/* 滚动条样式 */
.messages-container::-webkit-scrollbar,
.tools-section::-webkit-scrollbar,
.history-list::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track,
.tools-section::-webkit-scrollbar-track,
.history-list::-webkit-scrollbar-track {
  background: rgba(179, 224, 255, 0.3);
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb,
.tools-section::-webkit-scrollbar-thumb,
.history-list::-webkit-scrollbar-thumb {
  background: rgba(0, 102, 204, 0.5);
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover,
.tools-section::-webkit-scrollbar-thumb:hover,
.history-list::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 102, 204, 0.7);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .scheduling-modal {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .scheduling-content {
    flex-direction: column;
  }

  .tools-section {
    width: 100%;
    max-height: 300px;
  }

  .charts-container {
    grid-template-columns: 1fr;
  }

  .reference-section {
    grid-template-columns: 1fr;
  }
}
</style>
